/**
 * Size Chart Drawer Component
 * Matches filter drawer and cart drawer patterns
 */

class SizeChartDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawer = null;
    this.overlay = null;
    this.isOpen = false;
  }
  
  connectedCallback() {
    this.init();
  }
  
  init() {
    this.drawer = this.querySelector('.size-chart-drawer');
    if (!this.drawer) {
      this.createDrawer();
    }
    
    // Use global overlay (same as cart drawer)
    this.overlay = document.getElementById('overlay');
    
    // Event listeners
    const closeButton = this.drawer?.querySelector('.size-chart-drawer__close');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.close());
    }
    
    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        // Only close if size chart drawer is the one using the overlay
        if (this.overlay?.classList.contains('size-chart-overlay')) {
          this.close();
        }
      });
    }
    
    // ESC key
    this.drawer?.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.close();
      }
    });
  }
  
  createDrawer() {
    this.innerHTML = `
      <div class="size-chart-drawer-wrapper">
        <div class="size-chart-drawer" role="dialog" aria-modal="true" aria-label="Size chart" tabindex="-1">
          <div class="size-chart-drawer__header">
            <h2 class="size-chart-drawer__title">Size Chart</h2>
            <button type="button" class="size-chart-drawer__close modal__close-button link link--text focus-inset button--icon" aria-label="Close size chart">
              <svg class="icon icon-close" viewBox="0 0 32 32" fill="none" aria-hidden="true" focusable="false">
                <path stroke-linejoin="miter" stroke-linecap="butt" stroke-miterlimit="10" stroke-width="2" stroke="#000" fill="none" d="M22 10l-12 12"></path>
                <path stroke-linejoin="miter" stroke-linecap="butt" stroke-miterlimit="10" stroke-width="2" stroke="#000" fill="none" d="M22 22l-12-12"></path>
              </svg>
            </button>
          </div>
          <div class="size-chart-drawer__content">
            <p>Loading size chart...</p>
          </div>
        </div>
      </div>
    `;
    this.drawer = this.querySelector('.size-chart-drawer');
  }
  
  async open(content) {
    if (!this.drawer) {
      this.createDrawer();
      this.init();
    }

    // Load content if provided
    if (content) {
      const contentContainer = this.drawer.querySelector('.size-chart-drawer__content');
      if (contentContainer) {
        contentContainer.innerHTML = content;

        // Initialize unit toggle after content is loaded
        this.initUnitToggle();
      }
    }

    this.classList.add('active');
    if (this.overlay) {
      this.overlay.classList.add('active', 'size-chart-overlay');
    }

    lockBodyScroll();
    this.isOpen = true;

    // Focus trap
    this.trapFocus();

    // Focus first focusable element
    const firstFocusable = this.drawer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (firstFocusable) {
      firstFocusable.focus();
    }
  }

  initUnitToggle() {
    const toggleButtons = this.drawer.querySelectorAll('.size-chart-unit-toggle__button');
    const cmTable = this.drawer.querySelector('[data-unit-cm]');
    const inchTable = this.drawer.querySelector('[data-unit-inch]');

    if (!toggleButtons.length || !cmTable || !inchTable) return;

    toggleButtons.forEach(button => {
      button.addEventListener('click', () => {
        const unit = button.dataset.unit;

        // Update active button
        toggleButtons.forEach(btn => btn.classList.remove('active'));
        button.classList.add('active');

        // Toggle table visibility
        if (unit === 'cm') {
          cmTable.style.display = '';
          inchTable.style.display = 'none';
        } else {
          cmTable.style.display = 'none';
          inchTable.style.display = '';
        }
      });
    });
  }
  
  close() {
    this.classList.remove('active');
    if (this.overlay) {
      this.overlay.classList.remove('active');
      this.overlay.classList.add('overlay-fading');
      setTimeout(() => {
        this.overlay.classList.remove('size-chart-overlay', 'overlay-fading');
      }, 300);
    }
    
    unlockBodyScroll();
    this.isOpen = false;
    
    this.removeFocusTrap();

    if (window.location.hash === '#size-chart') {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }
  
  trapFocus() {
    const focusableElements = this.drawer.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    this.drawer.addEventListener('keydown', this.handleTrapFocus);
    this._trapFocusHandler = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };
    
    this.drawer.addEventListener('keydown', this._trapFocusHandler);
  }
  
  removeFocusTrap() {
    if (this._trapFocusHandler) {
      this.drawer.removeEventListener('keydown', this._trapFocusHandler);
      this._trapFocusHandler = null;
    }
  }
}

customElements.define('size-chart-drawer', SizeChartDrawer);

// Helper function to open drawer with content
window.openSizeChartDrawer = async function(content) {
  let drawer = document.querySelector('size-chart-drawer');
  if (!drawer) {
    drawer = document.createElement('size-chart-drawer');
    document.body.appendChild(drawer);
  }
  await drawer.open(content);
};

